import { useEffect, useMemo, useRef } from "react";
import useFocusTrap from "../../hooks/useFocusTrap";

const getModalSpecialtyLabel = (option) => {
  if (option.db === "PMR") {
    return "PM&R";
  }
  if (option.db === "Otolaryngology") {
    return "ENT";
  }
  return option.label;
};

export default function SpecialtySwitcherModal({
  isOpen,
  onClose,
  currentSpecialty,
  activeSpecialtyDb,
  onSpecialtySelect,
  specialtyOptions,
  isSaving,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const selectedOptionRef = useRef(null);

  useFocusTrap(isOpen, dialogRef, null, onClose);

  useEffect(() => {
    if (isOpen) {
      selectedOptionRef.current?.focus();
    }
  }, [isOpen, activeSpecialtyDb]);

  const sortedSpecialtyOptions = useMemo(
    () =>
      [...specialtyOptions].sort((left, right) =>
        getModalSpecialtyLabel(left).localeCompare(getModalSpecialtyLabel(right))
      ),
    [specialtyOptions]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f172a]/40 px-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="specialty-switch-title"
        className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold tracking-wide text-sky-700">
              Multi-specialty mode
            </p>
            <h2 id="specialty-switch-title" className="mt-2 text-xl font-bold text-slate-900">
              Update your active specialty
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 px-2 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            aria-label="Close specialty switcher"
          >
            X
          </button>
        </div>

        <p className="mt-3 text-sm text-slate-600">
          Current specialty:{" "}
          <span className="font-semibold text-slate-800">
            {currentSpecialty || "Not set"}
          </span>
        </p>

        <p id="specialty-switch-options-label" className="mt-4 block text-sm font-semibold text-slate-800">
          Choose specialty
        </p>
        {/* Toggle buttons, not radios: choosing one saves immediately, so arrow keys
            must not change the selection the way they do in a radio group. */}
        <div
          className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2"
          role="group"
          aria-labelledby="specialty-switch-options-label"
        >
          {sortedSpecialtyOptions.map((option) => {
            const isSelected = option.db === activeSpecialtyDb;
            return (
              <button
                key={option.db}
                ref={option.db === activeSpecialtyDb ? selectedOptionRef : null}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSpecialtySelect(option.db)}
                disabled={isSaving}
                className={`rounded-full border px-3 py-1.5 text-left text-sm font-semibold transition-colors ${
                  isSelected
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {getModalSpecialtyLabel(option)}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {isSaving ? "Updating specialty..." : "Tap once to switch instantly."}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            disabled={isSaving}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
